import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Flex, Text, Button, Input, InputGroup, InputLeftElement, InputRightElement,
    useToast, Badge, Textarea, Table, Thead, Tbody, Tr, Th, Td,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter, useDisclosure, VStack, HStack, Divider,
    IconButton, SimpleGrid, Select,
    Checkbox, TableContainer, Icon, Collapse, Skeleton, Tooltip,
    Menu, MenuButton, MenuList, MenuItem, Tabs, TabList, Tab,
} from '@chakra-ui/react';
import {
    Search, FileText, X, Filter, ChevronDown, ChevronUp,
    FileDown, FileCode, RefreshCw, CheckCircle, AlertCircle, Clock,
    MoreVertical, Printer, Copy,
} from 'lucide-react';
import ProdutoImagem from '../components/ProdutoImagem';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import TodasNotasFiscais from '../components/TodasNotasFiscais';
import PendenciasNotasFiscais from '../components/PendenciasNotasFiscais';
import ExportarXmlNfe from './ExportarXmlNfe';
import RelatorioNcmPadrao from './RelatorioNcmPadrao';
import EmissorNfeMl from './EmissorNfeMl';
import ParametrosFiscaisVenda from './ParametrosFiscaisVenda';
import { AuthContext } from '../contexts/auth';
import {
    getVendasManuais, getEstatisticasNfe, sincronizarStatusNfe,
    emitirNfeTaga, downloadNfeArquivo,
    downloadDanfePdf, downloadDanfeZpl,
} from '../services/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ACCENT = '#F68633';
const ACCENT_DARK = '#d97023';
const FOCUS_RING = '0 0 0 3px rgba(246,134,51,0.18)';

const STATUS_CFG = {
    AUTORIZADA: { label: 'Autorizada',  cor: 'green',  icon: CheckCircle,  emoji: '✅' },
    PENDENTE:   { label: 'Pendente',    cor: 'yellow', icon: Clock,         emoji: '⏱' },
    ERRO:       { label: 'Erro',        cor: 'red',    icon: AlertCircle,   emoji: '❌' },
};

const formatBRL = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return 'R$ 0,00';
    return 'R$ ' + n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const formatDateBR = (str) => {
    if (!str) return '—';
    try { return format(parseISO(str), 'dd/MM/yyyy HH:mm', { locale: ptBR }); }
    catch { return str; }
};

const OrigemBadge = ({ origem, conta }) => {
    if (!origem) return null;
    const lower = String(origem).toLowerCase();
    if (lower.includes('mercado livre') || lower.includes('mercado shops')) {
        return (
            <Badge colorScheme={String(conta) === '2' ? 'blue' : String(conta) === '3' ? 'green' : 'yellow'} fontSize="10px" px={1.5} borderRadius="md" variant="solid">
                ML {conta ?? ''}
            </Badge>
        );
    }
    if (lower.includes('ecommerce')) return <Badge colorScheme="teal" fontSize="10px" px={1.5} borderRadius="md">ECOMM</Badge>;
    if (lower.includes('taga')) return <Badge colorScheme="orange" fontSize="10px" px={1.5} borderRadius="md">TAGA</Badge>;
    return <Badge colorScheme="gray" fontSize="10px" px={1.5} borderRadius="md">{origem}</Badge>;
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CFG[status];
    if (!cfg) return null;
    return (
        <Badge
            colorScheme={cfg.cor} fontSize="10px" px={2} py={1}
            borderRadius="md" display="inline-flex" alignItems="center" gap={1}
            whiteSpace="nowrap"
        >
            <Icon as={cfg.icon} boxSize={3} />
            {cfg.label}
        </Badge>
    );
};

const ProdutoInfo = ({ produto, totalItens = 1 }) => {
    if (!produto) return <Text fontSize="xs" color="gray.400" fontStyle="italic">Sem item</Text>;
    return (
        <HStack align="flex-start" spacing={3}>
            <Box flexShrink={0}>
                <ProdutoImagem
                    produto={produto.produto_estoque}
                    origem={null}
                    width="44px" height="44px"
                    imagemAPI={produto.imagem_url || ''}
                />
            </Box>
            <VStack align="stretch" spacing={0.5} flex="1" minW={0}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.800" noOfLines={2} lineHeight={1.25}>
                    {produto.nome || 'Sem nome'}
                    {totalItens > 1 && <Text as="span" fontSize="10px" color="gray.500"> +{totalItens - 1}</Text>}
                </Text>
                <Flex wrap="wrap" columnGap={2} rowGap={0}>
                    {produto.id && <Text fontSize="10px" color="gray.500"><Text as="span" fontWeight="bold">SKU:</Text> {produto.id}</Text>}
                    {produto.codigo_global && <Text fontSize="10px" color="gray.500"><Text as="span" fontWeight="bold">Global:</Text> {produto.codigo_global}</Text>}
                </Flex>
            </VStack>
        </HStack>
    );
};

const ABAS = [
    { chave: 'vendas', label: 'Vendas' },
    { chave: 'todas', label: 'Todas as notas' },
    { chave: 'pendencias', label: 'Pendências' },
    { chave: 'exportar', label: 'Exportar XML' },
    { chave: 'ncm-padrao', label: 'NCM padrão' },
    { chave: 'emissor-ml', label: 'Emissor ML' },
    { chave: 'fiscal-venda', label: 'Fiscal (venda)' },
];

const NotasFiscais = () => {
    const toast = useToast();
    const { user } = useContext(AuthContext);
    const isAdmin = user?.tipo === 1;
    const [searchParams, setSearchParams] = useSearchParams();
    const aba = isAdmin ? Math.max(0, ABAS.findIndex((a) => a.chave === searchParams.get('aba'))) : 0;
    const mudarAba = (i) => setSearchParams(i === 0 ? {} : { aba: ABAS[i].chave });
    const [filtrosTodas, setFiltrosTodas] = useState(null);

    const [vendas, setVendas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingGlobal, setLoadingGlobal] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const [stats, setStats] = useState({ total: 0, autorizadas: 0, com_erro: 0, pendentes: 0 });

    // Filtros
    const [filtroBusca, setFiltroBusca] = useState('');
    const [filtroBuscaDeb, setFiltroBuscaDeb] = useState('');
    const buscaTimeoutRef = useRef(null);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroOrigem, setFiltroOrigem] = useState('');
    const [filtroConta, setFiltroConta] = useState('');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    const [filtroValorMin, setFiltroValorMin] = useState('');
    const [filtroValorMax, setFiltroValorMax] = useState('');
    const [filtrosAbertos, setFiltrosAbertos] = useState(false);

    // Seleção bulk
    const [selecionados, setSelecionados] = useState(new Set());

    // Modais
    const { isOpen: isEmitirOpen, onOpen: onEmitirOpen, onClose: onEmitirClose } = useDisclosure();
    const [vendaEmitir, setVendaEmitir] = useState(null);
    const [emitindo, setEmitindo] = useState(false);
    const [editPayload, setEditPayload] = useState(null);
    const [nfeResult, setNfeResult] = useState(null);

    const { isOpen: isDetalheOpen, onOpen: onDetalheOpen, onClose: onDetalheClose } = useDisclosure();
    const [vendaDetalhe, setVendaDetalhe] = useState(null);

    // Download tracking
    const [downloadingVendas, setDownloadingVendas] = useState(new Set());

    // Debounce busca
    useEffect(() => {
        if (buscaTimeoutRef.current) clearTimeout(buscaTimeoutRef.current);
        buscaTimeoutRef.current = setTimeout(() => setFiltroBuscaDeb(filtroBusca), 400);
        return () => { if (buscaTimeoutRef.current) clearTimeout(buscaTimeoutRef.current); };
    }, [filtroBusca]);

    // Fetch vendas
    const fetchVendas = useCallback(async () => {
        setLoading(true);
        try {
            const filters = { per_page: perPage };
            if (filtroBuscaDeb) filters.busca = filtroBuscaDeb;
            if (filtroStatus) filters.status_nf = filtroStatus;
            if (filtroOrigem) filters.origem = filtroOrigem;
            if (filtroConta) filters.conta = filtroConta;
            if (filtroDataInicio) filters.data_inicio = filtroDataInicio;
            if (filtroDataFim) filters.data_fim = filtroDataFim;
            if (filtroValorMin) filters.valor_min = filtroValorMin;
            if (filtroValorMax) filters.valor_max = filtroValorMax;

            const data = await getVendasManuais(page, filters);
            if (data) {
                setVendas(data.data || []);
                setTotalPages(data.last_page || 1);
                setTotal(data.total || 0);
            } else {
                setVendas([]);
            }
        } catch (e) {
            console.error(e);
            toast({ title: 'Erro ao carregar notas fiscais', status: 'error', duration: 3000 });
        } finally {
            setLoading(false);
        }
    }, [page, perPage, filtroBuscaDeb, filtroStatus, filtroOrigem, filtroConta, filtroDataInicio, filtroDataFim, filtroValorMin, filtroValorMax, toast]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await getEstatisticasNfe();
            if (res?.data?.data) setStats(res.data.data);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchVendas(); }, [fetchVendas]);
    useEffect(() => { fetchStats(); }, []);

    // Reset pagina ao mudar filtros
    useEffect(() => {
        if (page !== 1) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroBuscaDeb, filtroStatus, filtroOrigem, filtroConta, filtroDataInicio, filtroDataFim, filtroValorMin, filtroValorMax]);

    const limparFiltros = () => {
        setFiltroBusca('');
        setFiltroBuscaDeb('');
        setFiltroStatus('');
        setFiltroOrigem('');
        setFiltroConta('');
        setFiltroDataInicio('');
        setFiltroDataFim('');
        setFiltroValorMin('');
        setFiltroValorMax('');
    };

    const filtrosAtivos =
        (filtroBuscaDeb ? 1 : 0) + (filtroStatus ? 1 : 0) + (filtroOrigem ? 1 : 0) +
        (filtroConta ? 1 : 0) + (filtroDataInicio ? 1 : 0) + (filtroDataFim ? 1 : 0) +
        (filtroValorMin ? 1 : 0) + (filtroValorMax ? 1 : 0);

    // Seleção
    const toggleSelecionado = (id) => {
        setSelecionados((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleTodos = () => {
        const allIds = vendas.filter(v => v.status_nfe === 'AUTORIZADA').map(v => v.id);
        setSelecionados((prev) => {
            const allSel = allIds.every(id => prev.has(id));
            if (allSel) return new Set();
            return new Set(allIds);
        });
    };

    const temSelecao = selecionados.size > 0;

    // Ações
    const abrirEmitir = (venda) => {
        setVendaEmitir(venda);
        setEditPayload(null);
        setNfeResult(null);
        onEmitirOpen();
    };

    const emitirNfe = async () => {
        if (!vendaEmitir) return;
        setEmitindo(true);
        try {
            const resp = await emitirNfeTaga(vendaEmitir.id, editPayload);
            if (resp?.status >= 200 && resp?.status < 300) {
                setNfeResult({ sucesso: true, data: resp.data });
                toast({ title: 'NFe emitida com sucesso!', status: 'success', duration: 3000 });
                fetchVendas();
                fetchStats();
            } else {
                const errData = resp?.data || {};
                setNfeResult({ sucesso: false, data: errData });
                setEditPayload(errData.submitted_payload || null);
                toast({
                    title: 'Falha ao emitir NFe',
                    description: errData.message || errData.error || `HTTP ${resp?.status}`,
                    status: 'error', duration: 5000,
                });
            }
        } catch (e) {
            const errData = e?.response?.data || {};
            setNfeResult({ sucesso: false, data: errData });
            setEditPayload(errData.submitted_payload || null);
            toast({
                title: 'Falha ao emitir NFe',
                description: errData.message || e?.message,
                status: 'error', duration: 5000,
            });
        } finally {
            setEmitindo(false);
        }
    };

    const downloadArquivo = async (nfeId, tipo, vendaId) => {
        if (!nfeId) return;
        setDownloadingVendas(prev => new Set(prev).add(vendaId));
        try {
            const url = tipo === 'xml' ? `/nfe/xml/${nfeId}` : `/nfe/danfe/${nfeId}`;
            const res = await downloadNfeArquivo(url);
            if (!res?.data) throw new Error('Resposta vazia');
            const blob = new Blob([res.data], { type: tipo === 'xml' ? 'application/xml' : 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `nfe_${nfeId}.${tipo === 'xml' ? 'xml' : 'pdf'}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (e) {
            toast({ title: `Erro ao baixar ${tipo.toUpperCase()}`, description: e?.message, status: 'error', duration: 4000 });
        } finally {
            setDownloadingVendas(prev => {
                const n = new Set(prev); n.delete(vendaId); return n;
            });
        }
    };

    const downloadZplIndividual = async (vendaId) => {
        setDownloadingVendas(prev => new Set(prev).add(vendaId));
        try {
            const res = await downloadDanfeZpl([vendaId]);
            if (res?.error) {
                toast({ title: 'Erro ao gerar DANFE ZPL', description: res.error, status: 'error', duration: 4000 });
            }
        } finally {
            setDownloadingVendas(prev => {
                const n = new Set(prev); n.delete(vendaId); return n;
            });
        }
    };

    const downloadPdfIndividual = async (vendaId) => {
        setDownloadingVendas(prev => new Set(prev).add(vendaId));
        try {
            const res = await downloadDanfePdf([vendaId]);
            if (res?.error) {
                toast({ title: 'Erro ao gerar DANFE PDF', description: res.error, status: 'error', duration: 4000 });
            }
        } finally {
            setDownloadingVendas(prev => {
                const n = new Set(prev); n.delete(vendaId); return n;
            });
        }
    };

    const downloadBulk = async (tipo) => {
        const ids = Array.from(selecionados);
        if (ids.length === 0) return;
        setLoadingGlobal(true);
        try {
            const res = tipo === 'pdf' ? await downloadDanfePdf(ids) : await downloadDanfeZpl(ids);
            if (res?.error) {
                toast({ title: 'Erro no download em lote', description: res.error, status: 'error', duration: 4000 });
            } else {
                toast({ title: `${ids.length} DANFE(s) ${tipo.toUpperCase()} baixada(s)`, status: 'success', duration: 2500 });
            }
        } finally {
            setLoadingGlobal(false);
        }
    };

    const sincronizar = async (vendaId) => {
        setDownloadingVendas(prev => new Set(prev).add(vendaId));
        try {
            const res = await sincronizarStatusNfe(vendaId);
            if (res?.data?.success) {
                toast({ title: 'Status sincronizado', description: `Novo status: ${res.data.status}`, status: 'success', duration: 2500 });
                fetchVendas();
                fetchStats();
            } else {
                toast({ title: 'Falha ao sincronizar', description: res?.data?.error, status: 'warning', duration: 3500 });
            }
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message;
            toast({ title: 'Erro ao sincronizar', description: msg, status: 'error', duration: 4000 });
        } finally {
            setDownloadingVendas(prev => {
                const n = new Set(prev); n.delete(vendaId); return n;
            });
        }
    };

    const copiarTexto = (texto, label) => {
        navigator.clipboard.writeText(texto);
        toast({ title: `${label} copiado`, status: 'info', duration: 1500 });
    };

    // Card mobile
    const VendaCard = ({ v }) => {
        const isDownloading = downloadingVendas.has(v.id);
        const isAutorizada = v.status_nfe === 'AUTORIZADA';
        return (
            <Box bg="white" borderRadius="xl" p={3}
                boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                border="1px solid" borderColor="gray.200"
                borderLeft="3px solid"
                borderLeftColor={isAutorizada ? 'green.400' : v.status_nfe === 'ERRO' ? 'red.400' : 'yellow.400'}
            >
                <Flex justify="space-between" align="flex-start" mb={2} gap={2}>
                    <HStack spacing={2} flexWrap="wrap">
                        <OrigemBadge origem={v.origem} conta={v.conta} />
                        <StatusBadge status={v.status_nfe} />
                        {isAutorizada && (
                            <Checkbox
                                size="sm" colorScheme="orange"
                                isChecked={selecionados.has(v.id)}
                                onChange={() => toggleSelecionado(v.id)}
                            />
                        )}
                    </HStack>
                    <Text fontSize="10px" color="gray.500" textAlign="right" whiteSpace="nowrap">
                        {formatDateBR(v.created_at)}
                    </Text>
                </Flex>

                <Box mb={2}>
                    <ProdutoInfo produto={v.produto_principal} totalItens={v.total_itens} />
                </Box>

                <Flex justify="space-between" align="center" mb={2}>
                    <Box>
                        <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">Venda</Text>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                            #{v.id} {v.id_origem && `· ${v.id_origem}`}
                        </Text>
                    </Box>
                    <Box textAlign="right">
                        <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">Valor</Text>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800">{formatBRL(v.valor_total_pagamento)}</Text>
                    </Box>
                </Flex>

                {v.cliente && (
                    <Text fontSize="xs" color="gray.600" mb={2} noOfLines={1}>
                        <Text as="span" fontWeight="semibold">Cliente:</Text> {v.cliente.nome || '—'}
                    </Text>
                )}

                {v.venda_fiscal?.id_nf && (
                    <Flex align="center" gap={1} mb={2} wrap="wrap">
                        <Text fontSize="10px" color="gray.500" fontWeight="semibold">NFe:</Text>
                        <Text fontSize="10px" color="gray.700" fontFamily="mono">{v.venda_fiscal.id_nf}</Text>
                        <IconButton
                            icon={<Copy size={10} />} size="xs" variant="ghost" aria-label="Copiar"
                            onClick={() => copiarTexto(v.venda_fiscal.id_nf, 'ID NFe')}
                        />
                    </Flex>
                )}

                <Divider my={2} />

                {isAutorizada ? (
                    <SimpleGrid columns={3} gap={1}>
                        <Button size="xs" leftIcon={<FileDown size={12} />} variant="outline" colorScheme="blue"
                            isDisabled={isDownloading}
                            onClick={() => downloadPdfIndividual(v.id)}>
                            PDF
                        </Button>
                        <Button size="xs" leftIcon={<FileCode size={12} />} variant="outline" colorScheme="purple"
                            isDisabled={isDownloading}
                            onClick={() => downloadArquivo(v.venda_fiscal?.id_nf, 'xml', v.id)}>
                            XML
                        </Button>
                        <Button size="xs" leftIcon={<Printer size={12} />} variant="outline" colorScheme="teal"
                            isDisabled={isDownloading}
                            onClick={() => downloadZplIndividual(v.id)}>
                            ZPL
                        </Button>
                    </SimpleGrid>
                ) : (
                    <HStack spacing={1}>
                        <Button size="xs" flex={1} colorScheme="orange" onClick={() => abrirEmitir(v)} leftIcon={<FileText size={12} />}>
                            Emitir NFe
                        </Button>
                        {v.venda_fiscal?.id_nf && (
                            <Button size="xs" variant="outline" onClick={() => sincronizar(v.id)} isDisabled={isDownloading} leftIcon={<RefreshCw size={12} />}>
                                Sync
                            </Button>
                        )}
                    </HStack>
                )}
            </Box>
        );
    };

    return (
        <Box ml={{ base: 0, md: '64px' }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            {loadingGlobal && <Preloader />}
            <Box maxW="1400px" mx="auto" px={{ base: 3, md: 6 }} pt={{ base: 2, md: 3 }} pb={{ base: 4, md: 6 }}>

                {/* Header */}
                <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={3}
                    borderBottom="2px solid" borderColor="gray.200" mb={4} pb={2}
                    direction={{ base: 'column', md: 'row' }}>
                    <Box>
                        <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color="gray.800" lineHeight={1.15}>
                            Notas Fiscais
                        </Text>
                        <Text fontSize="11px" color="gray.500" mt="1px">
                            Emissão e gestão de NFes (ML, Ecommerce, TAGA)
                        </Text>
                    </Box>
                    {temSelecao && aba === 0 && (
                        <HStack spacing={2}>
                            <Text fontSize="xs" color="gray.600">{selecionados.size} selecionada(s)</Text>
                            <Button size="sm" leftIcon={<FileDown size={14} />} colorScheme="blue" variant="outline"
                                onClick={() => downloadBulk('pdf')}>
                                DANFEs PDF
                            </Button>
                            <Button size="sm" leftIcon={<Printer size={14} />} colorScheme="teal" variant="outline"
                                onClick={() => downloadBulk('zpl')}>
                                DANFEs ZPL
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSelecionados(new Set())}>Limpar</Button>
                        </HStack>
                    )}
                </Flex>

                {isAdmin && (
                    <Tabs index={aba} onChange={mudarAba} colorScheme="orange" mb={4}>
                        <TabList overflowX="auto" overflowY="hidden" sx={{ scrollbarWidth: 'none' }}>
                            {ABAS.map((a) => <Tab key={a.chave} fontSize="sm" whiteSpace="nowrap">{a.label}</Tab>)}
                        </TabList>
                    </Tabs>
                )}

                {aba === 1 && <TodasNotasFiscais key={JSON.stringify(filtrosTodas)} filtrosIniciais={filtrosTodas} />}
                {aba === 2 && <PendenciasNotasFiscais onVerNotas={(f) => { setFiltrosTodas(f); mudarAba(1); }} />}
                {aba === 3 && <ExportarXmlNfe />}
                {aba === 4 && <RelatorioNcmPadrao />}
                {aba === 5 && <EmissorNfeMl />}
                {aba === 6 && <ParametrosFiscaisVenda />}

                {aba === 0 && (<>
                {/* Cards de estatísticas */}
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3} mb={4}>
                    {[
                        { key: 'total',       label: 'Total',       valor: stats.total,       cor: 'gray'   },
                        { key: 'autorizadas', label: 'Autorizadas', valor: stats.autorizadas, cor: 'green'  },
                        { key: 'pendentes',   label: 'Pendentes',   valor: stats.pendentes,   cor: 'yellow' },
                        { key: 'com_erro',    label: 'Com erro',    valor: stats.com_erro,    cor: 'red'    },
                    ].map((s) => {
                        const ativo = (s.key === 'autorizadas' && filtroStatus === 'AUTORIZADA')
                                   || (s.key === 'pendentes' && filtroStatus === 'PENDENTE')
                                   || (s.key === 'com_erro' && filtroStatus === 'ERRO');
                        return (
                            <Box key={s.key}
                                bg="white" borderRadius="lg" p={3}
                                border="1px solid"
                                borderColor={ativo ? `${s.cor}.400` : 'gray.200'}
                                boxShadow={ativo ? '0 2px 6px rgba(0,0,0,0.06)' : '0 1px 2px rgba(0,0,0,0.04)'}
                                cursor={s.key === 'total' ? 'default' : 'pointer'}
                                _hover={s.key !== 'total' ? { borderColor: `${s.cor}.300` } : {}}
                                onClick={() => {
                                    if (s.key === 'total') return;
                                    const map = { autorizadas: 'AUTORIZADA', pendentes: 'PENDENTE', com_erro: 'ERRO' };
                                    setFiltroStatus(filtroStatus === map[s.key] ? '' : map[s.key]);
                                }}
                                transition="all 0.15s"
                            >
                                <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide" mb={0.5}>
                                    {s.label}
                                </Text>
                                <Text fontSize="xl" fontWeight="bold" color={`${s.cor}.600`}>{s.valor}</Text>
                            </Box>
                        );
                    })}
                </SimpleGrid>

                {/* Busca principal + toggle filtros */}
                <Flex gap={2} mb={3} direction={{ base: 'column', sm: 'row' }}>
                    <InputGroup size="md" flex="1">
                        <InputLeftElement><Search size={16} color="#9CA3AF" /></InputLeftElement>
                        <Input
                            placeholder="Buscar por venda, cliente, CPF/CNPJ, NFe, chave..."
                            value={filtroBusca}
                            onChange={(e) => setFiltroBusca(e.target.value)}
                            bg="white" borderRadius="lg" pr={filtroBusca ? 10 : 4}
                            _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}
                        />
                        {filtroBusca && (
                            <InputRightElement>
                                <IconButton
                                    icon={<X size={14} />} size="xs" variant="ghost"
                                    color="gray.400" _hover={{ color: 'gray.700', bg: 'gray.100' }}
                                    onClick={() => setFiltroBusca('')} aria-label="Limpar"
                                    borderRadius="full"
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>
                    <Button
                        leftIcon={<Filter size={14} />}
                        rightIcon={filtrosAbertos ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        size="md" variant="outline" bg="white"
                        onClick={() => setFiltrosAbertos(!filtrosAbertos)}
                    >
                        Filtros {filtrosAtivos > 0 && <Badge ml={2} colorScheme="orange" fontSize="9px">{filtrosAtivos}</Badge>}
                    </Button>
                </Flex>

                <Collapse in={filtrosAbertos} animateOpacity>
                    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={3} mb={3}>
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} gap={2}>
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Status</Text>
                                <Select size="sm" bg="white" value={filtroStatus}
                                    onChange={(e) => setFiltroStatus(e.target.value)}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                    <option value="">Todos</option>
                                    <option value="AUTORIZADA">✅ Autorizada</option>
                                    <option value="PENDENTE">⏱ Pendente</option>
                                    <option value="ERRO">❌ Erro</option>
                                </Select>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Origem</Text>
                                <Select size="sm" bg="white" value={filtroOrigem}
                                    onChange={(e) => setFiltroOrigem(e.target.value)}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                    <option value="">Todas</option>
                                    <option value="ML">Mercado Livre</option>
                                    <option value="ECOMMERCE">Ecommerce</option>
                                    <option value="TAGA">TAGA</option>
                                </Select>
                            </Box>
                            {filtroOrigem === 'ML' && (
                                <Box>
                                    <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Conta ML</Text>
                                    <Select size="sm" bg="white" value={filtroConta}
                                        onChange={(e) => setFiltroConta(e.target.value)}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                        <option value="">Todas</option>
                                        <option value="1">Conta 1</option>
                                        <option value="2">Conta 2</option>
                                        <option value="3">Conta 3</option>
                                    </Select>
                                </Box>
                            )}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Data inicial</Text>
                                <Input type="date" size="sm" bg="white" value={filtroDataInicio}
                                    onChange={(e) => setFiltroDataInicio(e.target.value)}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Data final</Text>
                                <Input type="date" size="sm" bg="white" value={filtroDataFim}
                                    onChange={(e) => setFiltroDataFim(e.target.value)}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Valor mín.</Text>
                                <Input type="number" size="sm" bg="white" placeholder="0,00" value={filtroValorMin}
                                    onChange={(e) => setFiltroValorMin(e.target.value)}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Valor máx.</Text>
                                <Input type="number" size="sm" bg="white" placeholder="0,00" value={filtroValorMax}
                                    onChange={(e) => setFiltroValorMax(e.target.value)}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                            </Box>
                        </SimpleGrid>
                        <Flex justify="flex-end" mt={3}>
                            <Button size="sm" variant="ghost" onClick={limparFiltros}>Limpar filtros</Button>
                        </Flex>
                    </Box>
                </Collapse>

                {/* Lista */}
                {loading ? (
                    <VStack spacing={2} align="stretch">
                        {[...Array(6)].map((_, i) => <Skeleton key={i} h="80px" borderRadius="xl" startColor="gray.100" endColor="gray.200" />)}
                    </VStack>
                ) : vendas.length === 0 ? (
                    <Flex direction="column" align="center" py={12} gap={3}>
                        <Flex w="60px" h="60px" borderRadius="full" bg="gray.100" align="center" justify="center">
                            <Icon as={FileText} boxSize={7} color="gray.400" />
                        </Flex>
                        <Text color="gray.500" fontSize="sm">Nenhuma venda encontrada</Text>
                    </Flex>
                ) : (
                    <>
                        {/* Desktop: tabela */}
                        <Box display={{ base: 'none', lg: 'block' }} bg="white" borderRadius="xl" overflow="hidden"
                            boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                            border="1px solid" borderColor="gray.200" mb={4}>
                            <TableContainer>
                                <Table size="sm">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th w="32px" whiteSpace="nowrap">
                                                <Checkbox size="sm" colorScheme="orange"
                                                    isChecked={vendas.filter(v => v.status_nfe === 'AUTORIZADA').length > 0 &&
                                                               vendas.filter(v => v.status_nfe === 'AUTORIZADA').every(v => selecionados.has(v.id))}
                                                    onChange={toggleTodos} />
                                            </Th>
                                            <Th whiteSpace="nowrap">Produto</Th>
                                            <Th whiteSpace="nowrap">Origem</Th>
                                            <Th whiteSpace="nowrap">Venda</Th>
                                            <Th whiteSpace="nowrap">Cliente</Th>
                                            <Th whiteSpace="nowrap">Data</Th>
                                            <Th isNumeric whiteSpace="nowrap">Valor</Th>
                                            <Th whiteSpace="nowrap">Status</Th>
                                            <Th whiteSpace="nowrap">NFe</Th>
                                            <Th whiteSpace="nowrap">Ações</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {vendas.map((v) => {
                                            const isAutorizada = v.status_nfe === 'AUTORIZADA';
                                            const isDownloading = downloadingVendas.has(v.id);
                                            return (
                                                <Tr key={v.id} _hover={{ bg: 'gray.50' }}>
                                                    <Td>
                                                        {isAutorizada && (
                                                            <Checkbox size="sm" colorScheme="orange"
                                                                isChecked={selecionados.has(v.id)}
                                                                onChange={() => toggleSelecionado(v.id)} />
                                                        )}
                                                    </Td>
                                                    <Td minW="260px" maxW="320px">
                                                        <ProdutoInfo produto={v.produto_principal} totalItens={v.total_itens} />
                                                    </Td>
                                                    <Td><OrigemBadge origem={v.origem} conta={v.conta} /></Td>
                                                    <Td fontSize="xs">
                                                        <Text fontWeight="semibold" color="gray.700">#{v.id}</Text>
                                                        {v.id_origem && <Text fontSize="10px" color="gray.500">{v.id_origem}</Text>}
                                                    </Td>
                                                    <Td fontSize="xs" color="gray.700" maxW="180px">
                                                        <Text noOfLines={1} title={v.cliente?.nome}>{v.cliente?.nome || '—'}</Text>
                                                    </Td>
                                                    <Td fontSize="10px" color="gray.500" whiteSpace="nowrap">{formatDateBR(v.created_at)}</Td>
                                                    <Td isNumeric fontSize="xs" fontWeight="semibold" color="gray.700">{formatBRL(v.valor_total_pagamento)}</Td>
                                                    <Td><StatusBadge status={v.status_nfe} /></Td>
                                                    <Td fontSize="10px" fontFamily="mono" color="gray.600">
                                                        {v.venda_fiscal?.id_nf ? (
                                                            <Tooltip label="Clique para copiar" hasArrow>
                                                                <Text cursor="pointer" onClick={() => copiarTexto(v.venda_fiscal.id_nf, 'ID NFe')} noOfLines={1} maxW="120px">
                                                                    {v.venda_fiscal.id_nf}
                                                                </Text>
                                                            </Tooltip>
                                                        ) : <Text color="gray.300">—</Text>}
                                                    </Td>
                                                    <Td>
                                                        {isAutorizada ? (
                                                            <HStack spacing={1}>
                                                                <Tooltip label="DANFE PDF" hasArrow>
                                                                    <IconButton
                                                                        icon={<FileDown size={14} />} size="xs" variant="ghost" colorScheme="blue"
                                                                        aria-label="PDF" isDisabled={isDownloading}
                                                                        onClick={() => downloadPdfIndividual(v.id)} />
                                                                </Tooltip>
                                                                <Tooltip label="DANFE ZPL (Zebra)" hasArrow>
                                                                    <IconButton
                                                                        icon={<Printer size={14} />} size="xs" variant="ghost" colorScheme="teal"
                                                                        aria-label="ZPL" isDisabled={isDownloading}
                                                                        onClick={() => downloadZplIndividual(v.id)} />
                                                                </Tooltip>
                                                                <Tooltip label="XML da NFe" hasArrow>
                                                                    <IconButton
                                                                        icon={<FileCode size={14} />} size="xs" variant="ghost" colorScheme="purple"
                                                                        aria-label="XML" isDisabled={isDownloading}
                                                                        onClick={() => downloadArquivo(v.venda_fiscal.id_nf, 'xml', v.id)} />
                                                                </Tooltip>
                                                                <Menu isLazy>
                                                                    <MenuButton as={IconButton} icon={<MoreVertical size={14} />} size="xs" variant="ghost" aria-label="Mais" />
                                                                    <MenuList fontSize="xs">
                                                                        <MenuItem icon={<RefreshCw size={12} />} onClick={() => sincronizar(v.id)}>Sincronizar status</MenuItem>
                                                                        <MenuItem icon={<Copy size={12} />} onClick={() => copiarTexto(v.venda_fiscal.id_nf, 'ID NFe')}>Copiar ID NFe</MenuItem>
                                                                        {v.venda_fiscal?.chave_nf && (
                                                                            <MenuItem icon={<Copy size={12} />} onClick={() => copiarTexto(v.venda_fiscal.chave_nf, 'Chave')}>Copiar chave</MenuItem>
                                                                        )}
                                                                    </MenuList>
                                                                </Menu>
                                                            </HStack>
                                                        ) : (
                                                            <HStack spacing={1}>
                                                                <Button size="xs" colorScheme="orange" leftIcon={<FileText size={12} />}
                                                                    onClick={() => abrirEmitir(v)}>
                                                                    Emitir
                                                                </Button>
                                                                {v.venda_fiscal?.id_nf && (
                                                                    <Tooltip label="Sincronizar status com API" hasArrow>
                                                                        <IconButton
                                                                            icon={<RefreshCw size={14} />} size="xs" variant="ghost"
                                                                            aria-label="Sync" isDisabled={isDownloading}
                                                                            onClick={() => sincronizar(v.id)} />
                                                                    </Tooltip>
                                                                )}
                                                            </HStack>
                                                        )}
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Box>

                        {/* Mobile: cards */}
                        <VStack display={{ base: 'flex', lg: 'none' }} spacing={3} align="stretch" mb={4}>
                            {vendas.map((v) => <VendaCard key={v.id} v={v} />)}
                        </VStack>

                        {/* Paginação */}
                        <HStack justify="space-between" wrap="wrap" gap={2} mb={4}>
                            <HStack spacing={2}>
                                <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                    {total} venda(s) · Página {page} / {totalPages}
                                </Text>
                                <HStack spacing={1}>
                                    <Text fontSize="xs" color="gray.500">Por página:</Text>
                                    <Select size="xs" w="70px" bg="white" value={perPage}
                                        onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </Select>
                                </HStack>
                            </HStack>
                            {totalPages > 1 && (
                                <HStack spacing={1}>
                                    <Button size="sm" variant="outline" isDisabled={page === 1}
                                        onClick={() => setPage(1)}>Primeira</Button>
                                    <Button size="sm" variant="outline" isDisabled={page === 1}
                                        onClick={() => setPage(page - 1)}>Anterior</Button>
                                    <Button size="sm" variant="outline" isDisabled={page === totalPages}
                                        onClick={() => setPage(page + 1)}>Próxima</Button>
                                    <Button size="sm" variant="outline" isDisabled={page === totalPages}
                                        onClick={() => setPage(totalPages)}>Última</Button>
                                </HStack>
                            )}
                        </HStack>
                    </>
                )}
                </>)}
            </Box>

            {/* Modal emitir NFe */}
            <Modal isOpen={isEmitirOpen} onClose={onEmitirClose} size={{ base: 'full', md: 'xl' }} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        Emitir NFe — Venda #{vendaEmitir?.id}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {!nfeResult && (
                            <>
                                <Text fontSize="sm" color="gray.700" mb={3}>
                                    Confirma a emissão da NFe para esta venda?
                                </Text>
                                {vendaEmitir && (
                                    <Box bg="gray.50" p={3} borderRadius="md" mb={3}>
                                        <SimpleGrid columns={2} gap={2} fontSize="xs">
                                            <Box><Text color="gray.500">Cliente</Text><Text fontWeight="semibold">{vendaEmitir.cliente?.nome || '—'}</Text></Box>
                                            <Box><Text color="gray.500">Valor</Text><Text fontWeight="semibold">{formatBRL(vendaEmitir.valor_total_pagamento)}</Text></Box>
                                            <Box><Text color="gray.500">Origem</Text><OrigemBadge origem={vendaEmitir.origem} conta={vendaEmitir.conta} /></Box>
                                            <Box><Text color="gray.500">Itens</Text><Text fontWeight="semibold">{vendaEmitir.total_itens}</Text></Box>
                                        </SimpleGrid>
                                    </Box>
                                )}
                                {editPayload && (
                                    <Box>
                                        <Text fontSize="xs" color="gray.600" mb={1} fontWeight="medium">
                                            Payload editável (JSON):
                                        </Text>
                                        <Textarea
                                            value={JSON.stringify(editPayload, null, 2)}
                                            onChange={(e) => {
                                                try { setEditPayload(JSON.parse(e.target.value)); } catch { /* ignore */ }
                                            }}
                                            fontFamily="mono" fontSize="10px" rows={14} bg="gray.50"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                        {nfeResult && nfeResult.sucesso && (
                            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" p={4}>
                                <Flex align="center" gap={2} mb={2}>
                                    <Icon as={CheckCircle} boxSize={5} color="green.500" />
                                    <Text fontSize="md" fontWeight="bold" color="green.800">NFe emitida com sucesso!</Text>
                                </Flex>
                                {nfeResult.data?.nfe?.id && (
                                    <Text fontSize="sm">ID: <Text as="span" fontFamily="mono" fontWeight="semibold">{nfeResult.data.nfe.id}</Text></Text>
                                )}
                            </Box>
                        )}
                        {nfeResult && !nfeResult.sucesso && (
                            <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={4}>
                                <Flex align="center" gap={2} mb={2}>
                                    <Icon as={AlertCircle} boxSize={5} color="red.500" />
                                    <Text fontSize="md" fontWeight="bold" color="red.800">Falha na emissão</Text>
                                </Flex>
                                <Text fontSize="xs" color="red.700" mb={2}>
                                    {nfeResult.data?.message || nfeResult.data?.error || 'Erro desconhecido'}
                                </Text>
                                {editPayload && (
                                    <Text fontSize="xs" color="gray.600">
                                        Você pode editar o payload acima e tentar de novo.
                                    </Text>
                                )}
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onEmitirClose} size="sm" mr={2}>
                            {nfeResult?.sucesso ? 'Fechar' : 'Cancelar'}
                        </Button>
                        {!nfeResult?.sucesso && (
                            <Button
                                colorScheme="orange" size="sm"
                                isLoading={emitindo} loadingText="Emitindo..."
                                onClick={emitirNfe}
                                bg={ACCENT} _hover={{ bg: ACCENT_DARK }}
                            >
                                {nfeResult ? 'Tentar de novo' : 'Confirmar Emissão'}
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default NotasFiscais;
